import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useTranslation } from "@/features/utils/i18n";
import { PencilSimpleIcon, SparkleIcon } from "@phosphor-icons/react";
import { useState, type ReactNode } from "react";

/**
 * "Remplir depuis mon site", as a proposal the owner reviews.
 *
 * Every place that reads the tenant's website — the company settings, the FAQ,
 * the services — works the same way: the site is read in the background, what
 * it suggests is listed with a checkbox each (all ticked), the owner may reword
 * an entry, and only « Appliquer » takes anything. These are the two pieces they
 * share, so the three reviews cannot drift apart: the dashed panel with its
 * title, status line and action, and the review list itself.
 */

/** The dashed strip every website reading lives in. */
export function WebsiteProposalPanel({
  title,
  description,
  error,
  action,
  children,
}: {
  title: ReactNode;
  description: ReactNode;
  /** Already worded for the owner — never the server's raw reason. */
  error?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="bg-muted/40 space-y-4 rounded-lg border border-dashed p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="flex items-center gap-2 text-sm font-medium">
            <SparkleIcon className="h-4 w-4" weight="duotone" />
            {title}
          </p>
          <p className="text-muted-foreground text-sm">{description}</p>
          {error ? <p className="text-destructive text-xs">{error}</p> : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>

      {children}
    </div>
  );
}

/** A text of the item the owner may reword before applying it. */
export type ProposalField = {
  name: string;
  label: string;
  value: string;
  multiline?: boolean;
};

/** Content that follows the owner's rewording, given the fields' values. */
type Rendered = ReactNode | ((values: Record<string, string>) => ReactNode);

export type ProposalItem = {
  key: string;
  /** The bold line: the field, the question, the service name. */
  label: Rendered;
  /** The proposed content, in its box. */
  value: Rendered;
  /** The muted line under it — typically what it replaces. */
  note?: ReactNode;
  /** Present when the item can be reworded; the edits reach `onApply`. */
  fields?: ProposalField[];
};

/** A ticked item, with the fields the owner changed (and only those). */
export type ProposalChoice = {
  key: string;
  edits: Record<string, string>;
};

/**
 * The review list: one checkbox per proposed item, everything ticked, an
 * optional quick edit, « Appliquer » and « Ignorer ». With no item it says the
 * site had nothing new and offers to close.
 *
 * Mount it with a `key` that changes with the proposal: a new reading is a new
 * selection, not the old one carried over.
 */
export function ProposalReview({
  intro,
  items,
  nothingNew,
  hint,
  applyLabel,
  busy,
  onApply,
  onDismiss,
}: {
  intro: ReactNode;
  items: ProposalItem[];
  nothingNew: ReactNode;
  hint?: ReactNode;
  applyLabel?: string;
  busy?: boolean;
  onApply: (choices: ProposalChoice[]) => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation("websiteProposal");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(items.map((item) => item.key)),
  );
  const [editing, setEditing] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, Record<string, string>>>(
    {},
  );

  if (items.length === 0) {
    return (
      <div className="flex items-center justify-between gap-3 border-t pt-4">
        <p className="text-muted-foreground text-sm">{nothingNew}</p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          disabled={busy}
        >
          {t("actions.close")}
        </Button>
      </div>
    );
  }

  const toggle = (key: string, checked: boolean) =>
    setSelected((previous) => {
      const next = new Set(previous);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });

  const edit = (key: string, field: string, value: string) =>
    setEdits((previous) => ({
      ...previous,
      [key]: { ...(previous[key] || {}), [field]: value },
    }));

  /** What the item shows now: the owner's wording where they gave one. */
  const valueOf = (item: ProposalItem, field: ProposalField) =>
    edits[item.key]?.[field.name] ?? field.value;

  const render = (item: ProposalItem, content: Rendered) =>
    typeof content === "function"
      ? content(
          Object.fromEntries(
            (item.fields || []).map((field) => [
              field.name,
              valueOf(item, field),
            ]),
          ),
        )
      : content;

  const apply = () =>
    onApply(
      items
        .filter((item) => selected.has(item.key))
        .map((item) => ({
          key: item.key,
          edits: Object.fromEntries(
            (item.fields || [])
              .filter((field) => {
                const value = valueOf(item, field).trim();
                return value && value !== field.value.trim();
              })
              .map((field) => [field.name, valueOf(item, field).trim()]),
          ),
        })),
    );

  return (
    <div className="space-y-4 border-t pt-4">
      <p className="text-sm">{intro}</p>

      <ul className="space-y-3">
        {items.map((item) => {
          const isEditing = editing === item.key;
          const id = `proposal-${item.key}`;
          return (
            <li key={item.key} className="flex items-start gap-3">
              <Checkbox
                id={id}
                checked={selected.has(item.key)}
                onCheckedChange={(checked) =>
                  toggle(item.key, checked === true)
                }
                className="mt-0.5"
              />
              <div className="min-w-0 flex-1 space-y-1 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <label htmlFor={id} className="cursor-pointer font-medium">
                    {render(item, item.label)}
                  </label>
                  {item.fields?.length ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="-mt-1 h-7 shrink-0 px-2 text-xs"
                      onClick={() => setEditing(isEditing ? null : item.key)}
                    >
                      <PencilSimpleIcon className="h-3.5 w-3.5" />
                      {isEditing ? t("actions.doneEditing") : t("actions.edit")}
                    </Button>
                  ) : null}
                </div>

                {isEditing ? (
                  <div className="space-y-2">
                    {item.fields!.map((field) => {
                      const fieldId = `${id}-${field.name}`;
                      return (
                        <div key={field.name} className="space-y-1">
                          <label
                            htmlFor={fieldId}
                            className="text-muted-foreground block text-xs"
                          >
                            {field.label}
                          </label>
                          {field.multiline ? (
                            <Textarea
                              id={fieldId}
                              rows={3}
                              value={valueOf(item, field)}
                              onChange={(e) =>
                                edit(item.key, field.name, e.target.value)
                              }
                            />
                          ) : (
                            <Input
                              id={fieldId}
                              value={valueOf(item, field)}
                              onChange={(e) =>
                                edit(item.key, field.name, e.target.value)
                              }
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <label
                    htmlFor={id}
                    className="bg-background block cursor-pointer rounded border px-2 py-1.5 whitespace-pre-line"
                  >
                    {render(item, item.value)}
                  </label>
                )}

                {item.note ? (
                  <span className="text-muted-foreground block text-xs">
                    {item.note}
                  </span>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          onClick={apply}
          disabled={selected.size === 0 || busy}
        >
          {applyLabel || t("actions.apply", { count: selected.size })}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onDismiss}
          disabled={busy}
        >
          {t("actions.ignore")}
        </Button>
        {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
      </div>
    </div>
  );
}

export const truncate = (value: string, max = 120): string =>
  value.length > max ? `${value.slice(0, max).trimEnd()}…` : value;

/** "https://studio-meraki.fr/contact" → "studio-meraki.fr". */
export const hostname = (url: string): string => {
  try {
    return new URL(
      url.startsWith("http") ? url : `https://${url}`,
    ).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};
